import { 
    gql, 
    useQuery 
} from "@apollo/client";
import { 
    FindAllInterestsQueryQuery, 
    QueryFindAllInterestsArgs
} from "../../../../../schemaTypes";
import { useCallback } from "react";

export const findAllInterestsQuery = gql`
  query FindAllInterestsQuery(
    $page: Int!
    $pageSize: Int!
    $duration: String
    $sortBy: String
    $sortOrder: String
  ) {
    findAllInterests(
      page: $page
      pageSize: $pageSize
      duration: $duration
      sortBy: $sortBy
      sortOrder: $sortOrder
    ) {
        data {
            id
            partner
            numberOfCustomers
            totalReceived
        }
        total
        page
        pageSize
        totalPages
    }
  }
`;

export interface WithFindAllInterests {
  data: FindAllInterestsQueryQuery["findAllInterests"]["data"]
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  loading: boolean;
  loadMore: () => void;
  hasMoreCampaigns: boolean;
}

interface Props {
  variables: QueryFindAllInterestsArgs;
  children: (data: WithFindAllInterests) => React.ReactElement | null;
}

export const FindAllInterestsController: React.FC<Props> = ({
  children,
  variables,
}) => {
  const { data, loading, fetchMore } = useQuery<
    FindAllInterestsQueryQuery,
    QueryFindAllInterestsArgs
  >(findAllInterestsQuery, {
    variables,
    notifyOnNetworkStatusChange: true,
  });

  const findAllInterestsData = data?.findAllInterests?.data || [];
  const page = data?.findAllInterests?.page || variables.page;
  const pageSize = data?.findAllInterests?.pageSize || variables.pageSize;
  const total = data?.findAllInterests?.total || 0;
  const totalPages = data?.findAllInterests?.totalPages || 0;
  const hasMoreCampaigns = page < totalPages;

  const loadMore = useCallback(() => {
    if (!hasMoreCampaigns) return;
    fetchMore({
      variables: {
        ...variables,
        page: page + 1,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult?.findAllInterests) {
          return prev;
        }
        return {
          ...fetchMoreResult,
          findAllInterests: {
            ...fetchMoreResult?.findAllInterests,
            data: [
              ...(prev?.findAllInterests?.data || []),
              ...(fetchMoreResult?.findAllInterests?.data || []),
            ],
          },
        };
      },
    });
  }, [fetchMore, variables, page, hasMoreCampaigns]);

  return children({
    data: findAllInterestsData as any,
    loading,
    loadMore,
    hasMoreCampaigns,
    page,
    pageSize,
    total,
    totalPages,
  });
};